import { Injectable, OnDestroy } from '@angular/core';
import {
	HttpTransportType,
	HubConnection,
	HubConnectionBuilder,
	HubConnectionState,
	ILogger,
	LogLevel, RetryContext
} from '@microsoft/signalr';
import { Subscription } from 'rxjs';
import { Utils } from '../../shared/utils';
import { TranslateService } from '../translate/translate.service';
import { config } from '../../shared/config';
import { SignalRHttpClient } from './signal-r-http-client';
import { AuthFacade } from '../../stores/auth/facades/auth.facade';
import { ModalService } from './modal.service';
import { SocketsManagerService } from '../../sockets-manager/services';
import { LiveDataRefreshEnum } from 'src/app/settings/models/live-data-refresh.enum';
import { CoreFacade } from 'src/app/stores/core/facades/core.facade';
import { map, take } from 'rxjs/operators';
import { SettingsModel } from '../models/settings.model';
import { Settings } from '../models/settings.enum';

@Injectable({
	providedIn: 'root'
})
export class HubService implements OnDestroy {
	protected readonly hubEndpoint: string;
	protected readonly maxReconnectCount: number = 5;
	protected readonly reconnectTimeout: number = 30_000;

	protected hubConnection: HubConnection;
	protected isDisconnecting: boolean;
	protected accessToken: string;
	protected subscriptions: Subscription = new Subscription();
	protected getUserSubscriptions: Subscription = new Subscription();
	protected reconnectCount = 0;
	protected infoModalContextText: string;

	protected utils: Utils = new Utils();
	protected timers: any[] = [];
	protected checkConnectionInterval;

	public isConnectionAllowed = true;
	private _livaDataOptionValue: LiveDataRefreshEnum;
	private _livaDataEnum = LiveDataRefreshEnum;

	constructor(
		hubEndpoint: string,
		protected modalService: ModalService,
		protected translate: TranslateService,
		protected signalRHttpClient: SignalRHttpClient,
		protected authFacade: AuthFacade,
		protected socketsManagerService: SocketsManagerService,
		protected _coreFacade: CoreFacade
	) {
		this.hubEndpoint = hubEndpoint;
		this.infoModalContextText = this.translate.data['hub.liveUpdatesUnavailable'];
		this.getUserSubscriptions.add(this.authFacade.getUser$.subscribe(user => this.accessToken = user ? user.accessToken : null));
		this._shouldReconnect();
	}

	public ngOnDestroy() {
		this.subscriptions.unsubscribe();
		this.getUserSubscriptions.unsubscribe();
		this.timers.forEach(x => clearTimeout(x));
		clearInterval(this.checkConnectionInterval);
	}

	private _shouldReconnect(): void {
		this.subscriptions.add(
			this._coreFacade.getSettings$.pipe(
				take(2),
				map((personalSettings: SettingsModel[]) => {
				  const setting = personalSettings?.find(
					(setting) => setting.key === Settings.TeagleLiveDataRefresh
				  );
				  return setting?.value;
				})
			  ).subscribe(liveDataRefresh => {
				this._livaDataOptionValue = liveDataRefresh as LiveDataRefreshEnum;
				if (this._livaDataOptionValue !== this._livaDataEnum.RequestResponse) {
					this.checkConnection();
				}
			  })
		)
	}

	public init() {
		if (this._livaDataOptionValue !== this._livaDataEnum.RequestResponse) {
			if (!this.isDisconnecting) {
				if (!this.hubConnection && this.isConnectionAllowed) {
					this.builder();
				}
				
				if (this.hubConnection.state === HubConnectionState.Disconnected && this.isConnectionAllowed) {
					this.onInit();
					this.start();
				}
	
				this.socketsManagerService.registerSocket({
					name: this.hubEndpoint,
					hubConnection: this.hubConnection
				});
			}
		}
	}

	public disconnect() {
		this.onDisconnect();
		this.timers.forEach(x => clearTimeout(x));
		if (!this.isDisconnecting && this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
			this.isDisconnecting = true;
			this.hubConnection.stop().then(() => {
				this.isDisconnecting = false;
				this.subscriptions.unsubscribe();
			});
		}
	}

	protected onDisconnect() {
	}

	protected onInit() {
	}


	protected isUserLogged(): boolean {
		return this.utils.isNotEmpty(this.accessToken);
	}

	protected builder() {
		const builder = new HubConnectionBuilder();
		this.hubConnection = builder
			.withUrl(config.apiUrl + this.hubEndpoint, {
				accessTokenFactory: () => {
					return this.accessToken;
				},
				httpClient: this.signalRHttpClient,
				skipNegotiation: this._livaDataOptionValue === this._livaDataEnum.WebSocket,
				transport: this._setTransport(),
				logger: new HubLogger(LogLevel.Warning),
			})
			.withAutomaticReconnect({
				nextRetryDelayInMilliseconds: (retryContext: RetryContext) => {
					if (retryContext.previousRetryCount === 0) {
						return 0;
					} else if (retryContext.previousRetryCount === 1) {
						return 10_000;
					} else if (retryContext.elapsedMilliseconds < this.maxReconnectCount * this.reconnectTimeout) {
						return this.reconnectTimeout;
					}
					
					return null;
				}
			})
			.configureLogging(new HubLogger(LogLevel.None))
			.build();
	}

	protected start() {
		Object.defineProperty(WebSocket, 'OPEN', { value: 1, });
		this.hubConnection.start().then(() => {
			this.onConnection();
			this.reconnectCount = 0;
			this.timers.forEach(x => clearTimeout(x));
		}).catch(error => {
			if (error?.status !== 403) {
				if (this.isUserLogged() && this.isConnectionAllowed && this.reconnectCount < this.maxReconnectCount) {
					this.timers.push(setTimeout(() => {
						this.reconnectCount++;
						this.start();
					}, this.reconnectTimeout));
				}
			}
		});
	}

	protected onConnection() {
	}

	protected checkConnection() {
		this.checkConnectionInterval = setInterval(() => {
			if (this.accessToken && this.isConnectionAllowed && this.hubConnection && this.hubConnection?.state === HubConnectionState.Disconnected) {
				this.start();
			} else if (this.accessToken && this.isConnectionAllowed && !this.hubConnection) {
				this.init();
			}
		}, 10_000);
	}

	private _setTransport() {
		switch (this._livaDataOptionValue) {
			case this._livaDataEnum.WebSocket: return HttpTransportType.WebSockets
			case this._livaDataEnum.LongPolling: return HttpTransportType.LongPolling
			case this._livaDataEnum.SSE: return HttpTransportType.ServerSentEvents
			case this._livaDataEnum.RequestResponse: return HttpTransportType.None
		}
	}
}

export class HubLogger implements ILogger {
	private readonly minimumLogLevel: LogLevel;

	// Public for testing purposes.
	public outputConsole: {
		error(message: any): void,
		warn(message: any): void,
		info(message: any): void,
		log(message: any): void,
	};

	constructor(minimumLogLevel: LogLevel) {
		this.minimumLogLevel = minimumLogLevel;
		this.outputConsole = console;
	}

	public log(logLevel: LogLevel, message: string): void {
		if (logLevel >= this.minimumLogLevel) {
			switch (logLevel) {
				case LogLevel.Critical:
				case LogLevel.Error:
					this.outputConsole.error(`${LogLevel[logLevel]}: ${message}`);
					break;
				case LogLevel.Warning:
					this.outputConsole.warn(`${LogLevel[logLevel]}: ${message}`);
					break;
				case LogLevel.Information:
					this.outputConsole.info(`${LogLevel[logLevel]}: ${message}`);
					break;
				default:
					// console.debug only goes to attached debuggers in Node, so we use console.log for Trace and Debug
					this.outputConsole.log(`${LogLevel[logLevel]}: ${message}`);
					break;
			}
		}
	}
}
